<template>
  <header class="region-navigation">
    <main-top-nav :sort-content-loading="sortContentLoading" @open-region-selector="openMore">
      <nav
        slot="regionNavigation"
        ref="regions"
        class="navigation-strip region-strip"
        aria-label="区域"
        @wheel="scrollHorizontally($event)"
      >
        <button
          v-for="region in regions"
          :key="String(region.regionId)"
          :disabled="disabled"
          :class="{ selected: String(region.regionId) === String(selectedRegionId) }"
          :title="region.regionName"
          @click="region.isMore ? openMore() : $emit('region', region)"
        >
          {{ region.regionName }}
        </button>
      </nav>
    </main-top-nav>
    <div class="category-line">
      <nav
        ref="categories"
        class="navigation-strip"
        aria-label="类别"
        @wheel="scrollHorizontally($event)"
        v-if="!searchMode"
      >
        <button
          v-for="category in categories"
          :key="category.code"
          :disabled="disabled"
          :class="{ selected: category.code === selectedCategoryCode }"
          @click="$emit('category', category)"
        >
          {{ category.name }}
        </button>
      </nav>
      <button
        v-if="!searchMode"
        class="search-entry"
        aria-label="搜索"
        :disabled="disabled"
        @click="openSearch"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m16 16 5 5" />
        </svg>
      </button>
      <form v-else class="region-search" @submit.prevent="$emit('search', { force: true })">
        <svg class="search-guide" viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg>
        <input
          ref="search"
          :value="keyword"
          placeholder="搜索设备或场景"
          aria-label="搜索设备或场景"
          @input="$emit('input', $event.target.value)"
          @blur="$emit('search')"
        />
        <button v-if="keyword" type="button" @mousedown.prevent @click="clear">清空</button>
        <button type="submit">搜索</button>
        <button type="button" @mousedown.prevent @click="$emit('close-search')">取消</button>
      </form>
    </div>
    <div
      v-if="moreVisible"
      class="region-picker-mask"
      @click.self="moreVisible = false"
      @keydown.esc="moreVisible = false"
    >
      <region-selector
        drawer
        @select="pick"
        @close="moreVisible = false"
        @manage="openManagement"
      />
    </div>
  </header>
</template>
<script>
import MainTopNav from '@/components/content/miantopnav/MaintopNav'
import RegionSelector from './region-selector'
import { requestNextRegionNavigationPage } from '@/common/region-navigation-data'
export default {
  components: { MainTopNav, RegionSelector },
  props: {
    sortContentLoading: Boolean,
    regions: Array,
    categories: Array,
    selectedRegionId: [String, Number],
    selectedCategoryCode: String,
    searchMode: Boolean,
    keyword: String,
    disabled: Boolean
  },
  data() {
    return { moreVisible: this.$route.query.regionSelector === '1' }
  },
  mounted() {
    this.$nextTick(() => this.align(this.$refs.regions))
  },
  watch: {
    '$route.query.regionSelector'(value) { if (value === '1') this.moreVisible = true },
    moreVisible(value) {
      if (!value && this.$route.query.regionSelector === '1') {
        const query = { ...this.$route.query }
        delete query.regionSelector
        this.$router.replace({ path: this.$route.path, query })
      }
      if (!value)
        this.$nextTick(() => {
          const trigger = this.$el.querySelector('.region-drawer-trigger')
          if (trigger) trigger.focus()
        })
    },
    selectedRegionId() {
      this.$nextTick(() => this.align(this.$refs.regions))
    },
    regions() {
      this.$nextTick(() => this.align(this.$refs.regions))
    },
    selectedCategoryCode() {
      this.$nextTick(this.ensureCategoryVisible)
    }
  },
  methods: {
    async openManagement() {
      await this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, regionSelector: '1' } })
      this.$router.push({ path: '/RegionManage', query: { type: 'manage', returnToSelector: '1' } })
    },
    scrollHorizontally(event) {
      const el = event.currentTarget
      if (el.scrollWidth > el.clientWidth && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        event.preventDefault()
        el.scrollLeft += event.deltaY
      }
    },
    openMore() {
      this.moreVisible = true
    },
    pick(region) {
      this.$emit('region', region)
      this.moreVisible = false
    },
    loadMore() {
      return requestNextRegionNavigationPage(this).catch(e =>
        this.$showTipsToast(3, e.message || '加载区域失败')
      )
    },
    onScroll(e) {
      const el = e.target
      if (
        this.$store.state.regionnavigation.hasMore &&
        !this.$store.state.regionnavigation.loading &&
        el.scrollHeight - el.scrollTop - el.clientHeight < 300
      )
        this.loadMore()
    },
    ensureCategoryVisible() {
      this.align(this.$refs.categories)
    },
    align(el) {
      if (!el) return
      const selected = el.querySelector('.selected')
      if (!selected) return
      if (el === this.$refs.regions) {
        el.scrollLeft = selected.offsetLeft - (el.clientWidth - selected.offsetWidth) / 2
        return
      }
      if (selected.offsetLeft < el.scrollLeft) el.scrollLeft = selected.offsetLeft
      else if (selected.offsetLeft + selected.offsetWidth > el.scrollLeft + el.clientWidth)
        el.scrollLeft = selected.offsetLeft + selected.offsetWidth - el.clientWidth
    },
    openSearch() {
      this.$emit('open-search')
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
    },
    clear() {
      this.$emit('clear')
      this.$refs.search.focus()
    },
    dismissSearchKeyboard() {
      if (this.$refs.search) this.$refs.search.blur()
    }
  }
}
</script>
<style scoped>
.region-navigation {
  flex: none;
  padding: 0 0 14px;
}
.navigation-strip {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 8px;
  position: relative;
  scrollbar-width: none;
  min-width: 0;
  overscroll-behavior-x: contain;
}
.navigation-strip::-webkit-scrollbar {
  display: none;
}
.navigation-strip button {
  flex: none;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: 0;
  font: inherit;
  cursor: pointer;
}
.region-strip {
  height: 50px;
  align-items: center;
  gap: 12px;
}
.region-strip::before,
.region-strip::after {
  content: '';
  flex: 0 0 calc(50% - 52px);
}
.region-strip button {
  position: relative;
  height: 44px;
  min-width: 104px;
  padding: 0 10px 5px;
  color: rgba(255, 255, 255, 0.68);
  background: none;
  font-size: 16px;
  border-radius: 0;
}
.region-strip button.selected {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}
.region-strip button.selected::after {
  content: '';
  position: absolute;
  bottom: 3px;
  left: calc(50% - 9px);
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: #fff;
}
.category-line {
  height: 54px;
  box-sizing: border-box;
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0 8px;
}
.category-line nav {
  flex: 1;
}
.category-line .navigation-strip button {
  padding: 6px 16px;
  min-width: 56px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.035);
  color: rgba(255, 255, 255, 0.68);
  font-size: 14px;
  line-height: 20px;
}
.category-line .navigation-strip button.selected {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  font-weight: 700;
  font-size: 15px;
}
.search-entry {
  width: 36px;
  height: 32px;
  border: 0;
  border-radius: 18px;
  padding: 6px;
  color: rgba(255, 255, 255, 0.68);
  background: rgba(255, 255, 255, 0.035);
  cursor: pointer;
  flex: none;
}
.search-entry svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
}
.region-search {
  align-items: center;
  height: 42px;
  min-height: 0;
  color: white;
  display: flex;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 4px 12px;
}
.region-search input {
  color: white;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 5px 8px;
  font: inherit;
}
.search-guide { width: 18px; height: 18px; flex: none; fill: none; stroke: rgba(255,255,255,.68); stroke-width: 1.8; }
.region-search input::placeholder { color: rgba(255,255,255,.6); }
.region-search button {
  padding: 6px 8px;
  border: 0;
  background: none;
  color: rgba(255,255,255,.85);
  cursor: pointer;
  font: inherit;
  white-space: nowrap;
}
.region-picker-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 10000;
  display: flex;
  align-items: stretch;
}
button:disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
